import { useUser } from "../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Settings,
  LogOut,
  Menu,
  Award,
  ChevronDown,
  Lock,
} from "lucide-react";
import { useState } from "react";
import { ChangePasswordModal } from "./auth/ChangePasswordModal";

interface SidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
}

export function Sidebar({ activeSection, setActiveSection }: SidebarProps) {
  const { currentUser, logout } = useUser();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const menuItems: Array<{
    id: string;
    label: string;
    icon: any;
    path?: string;
    subMenu?: Array<{ id: string; label: string; path: string }>;
    style?: React.CSSProperties;
  }> = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      path: "/dashboard",
    },
    {
      id: "modules",
      label: "Modules",
      icon: BookOpen,
      path: "/modules",
    },
    {
      id: "users",
      label: "Users",
      icon: Users,
      path: "/users",
    },
    ...(currentUser?.role === 'corporate_admin' ? [
      {
        id: "belt-management",
        label: "Belts",
        icon: Award,
        path: "/belt-management",
      }
    ] : []),
    {
      id: "reports",
      label: "Reports",
      icon: Settings,
      subMenu: [
        {
          id: "seller-training-progress",
          label: "Seller Training Progress",
          path: "/reports/seller-training-progress"
        }
      ]
    }
  ];

  return (
    <div
      className={`bg-card border-r h-screen transition-all duration-300 flex flex-col ${
        isCollapsed ? "w-20" : "w-64"
      }`}
    >
      <div className="p-4 flex items-center justify-between">
        {!isCollapsed && (
          <div>
          <h2 className="text-xl font-semibold">EPaathshaala</h2>
            {currentUser?.distributorName && (
              <>
              <p className="text-xs text-blue-600 mt-1">{currentUser.distributorName}</p>
                {currentUser.distributorCode && (
                  <p className="text-xs text-muted-foreground">{currentUser.distributorCode}</p>
                )}
              </>
            )}
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
      <div className="p-4">
        <div className="space-y-2">
          {menuItems.map((item) => (
            <div key={item.id}>
              <Button
                variant={activeSection === item.id ? "secondary" : "ghost"}
                className={`w-full justify-start flex items-center ${isCollapsed ? "px-2" : "px-4"}`}
                style={item.style}
                onClick={() => {
                  setActiveSection(item.id);
                  if (item.subMenu) {
                    setOpenSubMenu(openSubMenu === item.id ? null : item.id);
                  } else if (item.path) {
                    setOpenSubMenu(null);
                    navigate(item.path);
                  }
                }}
              >
                <item.icon className="h-5 w-5" />
                {!isCollapsed && (
                  <>
                    <span className="ml-2">{item.label}</span>
                    {item.subMenu && (
                      <ChevronDown
                        className={`ml-auto h-4 w-4 transition-transform duration-200 ${openSubMenu === item.id ? "rotate-180" : "rotate-0"}`}
                      />
                    )}
                  </>
                )}
              </Button>
              {/* Sub-menu rendering with animation */}
              <div
                className={`overflow-hidden transition-all duration-300 ${item.subMenu && !isCollapsed ? (openSubMenu === item.id ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0') : ''}`}
                style={{ marginLeft: item.subMenu && !isCollapsed ? 32 : 0 }}
              >
                {item.subMenu && !isCollapsed && (
                  <div className="mt-1 space-y-1">
                    {item.subMenu.map((sub) => (
                      <Button
                        key={sub.id}
                        variant={activeSection === sub.id ? "secondary" : "ghost"}
                        className="w-full justify-start px-2 text-left text-sm"
                        onClick={() => {
                          setActiveSection(sub.id);
                          navigate(sub.path);
                        }}
                      >
                        {sub.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>

      <div className="mt-auto border-t">
        <div className="p-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-lg font-semibold">
              {currentUser?.name?.charAt(0) || "U"}
            </span>
          </div>
          {!isCollapsed && (
            <div className="flex-1">
              <p className="text-base font-semibold leading-tight">{currentUser?.name}</p>
              <p className="text-xs text-muted-foreground">{currentUser?.username}</p>
              <p className="text-xs text-muted-foreground capitalize">{currentUser?.role}</p>
            </div>
          )}
        </div>
        
        {!isCollapsed && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start mb-2"
            onClick={() => setShowChangePasswordModal(true)}
          >
            <Lock className="h-4 w-4 mr-2" />
            Change Password
          </Button>
        )}
        
        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={handleLogout}
        >
          <LogOut className="h-5 w-5" />
          {!isCollapsed && <span className="ml-2">Logout</span>}
        </Button>
        </div>
      </div>

      <ChangePasswordModal 
        open={showChangePasswordModal} 
        onOpenChange={setShowChangePasswordModal} 
      />
    </div>
  );
} 